import React from 'react';
import _ from 'lodash';
import {Switch} from 'react-router-dom';
import {PrivateRoute} from './lib/auth/PrivateRoute';
import {ProductListView} from '~/lib/product';
import {generateQueryString} from '~/lib/routing';
import BrandTreezPage, {BrandTreezPageParameters} from '~/modules/pages/brandTreez/BrandTreezPage';
import HomePage from '~/modules/pages/home/HomePage';
import OrganizationAnalytics from '~/modules/pages/organizationAnalytics/OrganizationAnalytics';
import OrganizationCatalog, {
    CentralCatalogPageParameters,
} from '~/modules/pages/organizationCatalog/OrganizationCatalog';
import Stores from '~/modules/pages/stores/Stores';
import {TitleComponent} from './TitleComponent';
import ProductMerge, {MergeParameter} from './modules/pages/productMerge/ProductMerge';
import {PriceTierManagement} from './modules/pages/priceTiers/PriceTierManagement';
import {EditPriceTier} from './modules/pages/priceTiers/EditPriceTier';

export const ORGANIZATION_ANALYTICS_ROUTE = '/:organizationId/analytics';
export const ORGANIZATION_CATALOG_ROUTE = '/:organizationId/catalog';
export const PRICE_TIER_MANAGEMENT_ROUTE = '/:organizationId/priceTiers';
export const EDIT_PRICE_TIER_ROUTE = '/:organizationId/priceTiers/edit/:id?';
export const STORE_PAGE_ROUTE = '/:organizationId/stores/:storeId';
export const STORES_PAGE_ROUTE = '/:organizationId/stores';
export const BRAND_TREEZ_ROUTE = '/:organizationId/brandTreez';
export const MERGE_PRODUCTS_ROUTE = '/:organizationId/mergeProducts';

export interface BaseParameters {
    organizationId: number;
}

const withTitle = ({component: Component, title}) => {
    return class Title extends React.Component {
        render() {
            return (
                <>
                    <TitleComponent title={title} />
                    <Component {...this.props} />
                </>
            );
        }
    };
};

const OrganizationCatalogComponent = withTitle({
    component: OrganizationCatalog,
    title: 'Central Catalog View',
});
const BrandTreezPageComponent = withTitle({component: BrandTreezPage, title: 'BrandTreez Catalog'});
const StoresComponent = withTitle({component: Stores, title: 'Stores List'});
const ProductMergeComponent = withTitle({component: ProductMerge, title: 'Merge Products'});
const PriceTierManagementComponent = withTitle({
    component: PriceTierManagement,
    title: 'Pricing Management',
});
const EditPriceTierComponent = withTitle({component: EditPriceTier, title: 'Edit Price Tier'});

const Routes = () => {
    return (
        <Switch>
            <PrivateRoute
                exact
                path={ORGANIZATION_ANALYTICS_ROUTE}
                component={OrganizationAnalytics}
            />
            <PrivateRoute
                exact
                path={ORGANIZATION_CATALOG_ROUTE}
                component={OrganizationCatalogComponent}
            />
            <PrivateRoute
                exact
                path={PRICE_TIER_MANAGEMENT_ROUTE}
                component={PriceTierManagementComponent}
            />
            <PrivateRoute exact path={EDIT_PRICE_TIER_ROUTE} component={EditPriceTierComponent} />
            <PrivateRoute exact path={STORES_PAGE_ROUTE} component={StoresComponent} />
            <PrivateRoute exact path={BRAND_TREEZ_ROUTE} component={BrandTreezPageComponent} />
            <PrivateRoute exact path={MERGE_PRODUCTS_ROUTE} component={ProductMergeComponent} />
            <PrivateRoute component={HomePage} />
        </Switch>
    );
};

export interface RouteDetails {
    pathname: string;
    search?: string;
}

interface MergeProductRouteOptions {
    brandTreezProductId?: number;
    hideExactMatch?: boolean;
    productToMergeToId?: number;
}

const {
    PRODUCTS: MERGE_PRODUCTS,
    BRAND_PRODUCT: BRAND_PRODUCT_TO_MERGE_WITH,
    HIDE_EXACT_MATCH,
    PRODUCT_TO_MERGE_TO,
} = MergeParameter;

export const generateProductMergeRoute = (
    organizationId: number,
    productIds: number[],
    options: MergeProductRouteOptions = {}
): RouteDetails => {
    const {brandTreezProductId, hideExactMatch, productToMergeToId} = options;

    const params = _.omitBy(
        {
            products: productIds,
            brandProduct: brandTreezProductId,
            hideExactMatch,
            productToMergeTo: productToMergeToId,
        },
        (value, key) => {
            switch (key) {
                case MERGE_PRODUCTS:
                    return _.isEmpty(value) || !_.isArray(value);
                case BRAND_PRODUCT_TO_MERGE_WITH:
                    return !_.isNumber(value) || _.isNaN(value);
                case HIDE_EXACT_MATCH:
                    return !_.isBoolean(value);
                case PRODUCT_TO_MERGE_TO:
                    return !_.isNumber(value);
                default:
                    console.error(
                        `Parameter passed to Merge Route generator is invalid: ${key}: ${value}`
                    );
                    return true;
            }
        }
    );

    const pathname = `/${organizationId}/mergeProducts`;
    const search = generateQueryString(params);

    return {
        pathname,
        search,
    };
};

export const generateBrandTreezRoute = (
    organizationId: number,
    queryParams?: Partial<BrandTreezPageParameters>
): RouteDetails => {
    return {
        pathname: `/${organizationId}/brandTreez`,
        search: generateQueryString(queryParams),
    };
};

export const generateOrganizationAnalyticsRoute = (organizationId: number): RouteDetails => {
    return {
        pathname: `/${organizationId}/analytics`,
    };
};

export const generateOrganizationCatalogRoute = (
    organizationId: number,
    pageParameters: CentralCatalogPageParameters = {
        page: '1',
        pageSize: '10',
    }
): RouteDetails => {
    const pathname = `/${organizationId}/catalog`;
    let search = '';

    const {page = 1, view = ProductListView.STORE_MATRIX, pageSize = 25} = pageParameters;

    if (pageParameters != null) {
        search = generateQueryString({
            ...pageParameters,
            page,
            view,
            pageSize,
        });
    }

    return {
        pathname,
        search,
    };
};

export const generateStoresPageRoute = (organizationId: number): RouteDetails => {
    return {
        pathname: `/${organizationId}/stores`,
    };
};

export const generatePriceTierManagementRoute = (organizationId: number): RouteDetails => {
    return {
        pathname: `/${organizationId}/priceTiers`,
    };
};

export const generateRelativeEditPriceTierRoute = (tierId?: number): RouteDetails => {
    if (tierId) {
        return {
            pathname: `${location.pathname}/edit/${tierId}`,
        };
    }

    return {
        pathname: `${location.pathname}/edit/`,
    };
};

export default Routes;
